import { compareAsc } from "date-fns";

import {
  createAppSelector,
  selectActivities,
  selectCurrentCourseId,
  selectPageOutcomes,
  selectPages,
  selectTopics,
} from "./base";
import { selectAllAssessmentInfo } from "./computed";
import { selectAuthorizedPages, selectCurrentCourse, selectPagesForCurrentCourse } from "./course";
import { Capability, hasCourseCapability, selectCurrentUserRole } from "./permissions";
import { getSortedPages, sortActivities, sortTopicActivities } from "./sorting";
import {
  ActivityWithPagesType,
  PageWithDueDateType,
  TopicWithActivitiesType,
  TopicWithPagesType,
} from "./types";

import { AssessmentStatus } from "api/api-data-types";
import { isDateWithinLastXDays } from "components/materials/page/helpers";
import { ActivityPageType, CoreUserRoleType, PageType, TopicType } from "components/server-types";
import { DEFAULT_ORDER_FN } from "mds/hooks/use-ordered";
import { RootState } from "store";
import { isCoursePageCategory } from "utils/page";

// This is different from selectPagesForCurrentCourse in that it also selects topics-related pages
export const selectAllPagesWithDueDates = createAppSelector(
  [selectAuthorizedPages],
  (pages) => pages.filter((page) => page.due_at) as PageWithDueDateType[],
);

export const selectAllRecentlyAssessedPages = createAppSelector(
  [
    (_state, daysAgo: number) => daysAgo,
    (state, _daysAgo: number, role?: CoreUserRoleType) => selectAuthorizedPages(state, role),
  ],
  (daysAgo, pages) =>
    pages
      .filter((page) => isDateWithinLastXDays(page.assessments_published_at, daysAgo))
      .sort((a, b) => compareAsc(a.assessments_published_at, b.assessments_published_at)),
);

export const selectReadyForAssessmentPages = createAppSelector(
  [selectAuthorizedPages, selectAllAssessmentInfo],
  (pages, statuses) =>
    pages
      .filter((page) => statuses[page.id]?.status === AssessmentStatus.ASSESSABLE)
      .sort((a, b) => compareAsc(a.due_at, b.due_at)),
);

export const selectPagesForTopic = createAppSelector(
  [(state, topicId: string) => topicId, selectPages, (state) => state.activities],
  (topicId, pages, activities) => {
    return pages.filter((page) => {
      const activity = activities[page.activity_id];
      return activity && activity.topic_id === topicId;
    });
  },
);

export const selectPagesForOutcome = createAppSelector(
  [(state, courseOutcomeId: string) => courseOutcomeId, selectPages, selectPageOutcomes],
  (courseOutcomeId, pages, pageOutcomes) => {
    return pages.filter((page) =>
      pageOutcomes.some((po) => po.course_outcome_id === courseOutcomeId && po.page_id === page.id),
    );
  },
);

export const selectChunkedPagesForOutcomeByTopic = createAppSelector(
  [
    (state, courseOutcomeId: string) => selectPagesForOutcome(state, courseOutcomeId),
    selectActivities,
    selectTopics,
  ],
  (pages, activities, topics) => {
    const byTopic = topics.map((topic) => {
      const topicPages = pages.filter((page) => {
        const activity = activities.find((act) => act.id === page.activity_id);
        return activity?.topic_id === topic.id;
      });
      return { ...topic, pages: topicPages };
    });
    return byTopic;
  },
);

export const selectTopicsForCurrentCourse = createAppSelector(
  [selectTopics, selectCurrentCourse],
  (topics, currentCourse): TopicType[] =>
    topics.filter((topic) => topic.course_id === currentCourse?.id),
);

/**
 * This selector is very similar to `selectTopicsForCurrentCourse`, but it filters out topics
 * that should not be visible to the current user.
 * For instructors, the result of this selector is identical to `selectTopicsForCurrentCourse`.
 * But for students, this selector will filter out topics that have no released pages.
 */
export const selectVisibleTopicsForCurrentCourse = createAppSelector(
  [selectPages, (state) => state.activities, selectTopicsForCurrentCourse, selectCurrentUserRole],
  (pages: PageType[], activities, topicsForCurrentCourse: TopicType[], userRole) => {
    if (hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE)) {
      return topicsForCurrentCourse;
    }

    const releasedPages = pages.filter((page) => page.released_at);

    return topicsForCurrentCourse.filter((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicHasReleasedPages = releasedPages.some((page) => {
        const activity = activities[page.activity_id];
        return activity && activity.topic_id === topic.id;
      });
      return topicHasReleasedPages;
    });
  },
);

export const selectVisibleTopicsWithPages = createAppSelector(
  [selectPages, selectActivities, selectTopicsForCurrentCourse, selectCurrentUserRole],
  (pages, activities, topicsForCurrentCourse, userRole) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    const topicsWithPages = topicsForCurrentCourse.map((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicActivities = activities.filter((activity) => activity.topic_id === topic.id);
      const topicPages = pages.filter((page) =>
        topicActivities.some((activity) => activity.id === page.activity_id),
      );
      const released_page_count = topicPages.filter((page) => page.released_at).length;
      const unreleased_page_count = topicPages.filter((page) => !page.released_at).length;

      return {
        ...topic,
        pages: topicPages,
        released_page_count,
        unreleased_page_count,
      } as TopicWithPagesType;
    });

    return canAuthorCourse
      ? topicsWithPages
      : topicsWithPages.filter((t) => t.released_page_count > 0);
  },
);

export const selectTopicById = (state: RootState, topicId: string) => state.topics[topicId];

export const selectHasImported = createAppSelector(
  [(state, courseId: string) => courseId, selectTopics, selectActivities, selectPages],
  (courseId, topics, activities, pages) => {
    const courseTopics = topics.filter((topic) => topic.course_id === courseId);
    if (courseTopics.some((topic) => topic.progenitor_id)) {
      return true;
    }

    if (
      activities.some(
        (activity) =>
          courseTopics.find((topic) => topic.id === activity.topic_id) && activity.progenitor_id,
      )
    ) {
      return true;
    }

    if (pages.some((page) => page.course_id === courseId && page.progenitor_id)) {
      return true;
    }

    return false;
  },
);

export const selectSortedTopicActivitiesByTopicId = createAppSelector(
  [(state, topicId: string) => topicId, selectActivities, selectPages, selectCurrentUserRole],
  (topicId, activities, pages, userRole) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    return sortTopicActivities(topicId, activities, pages, canAuthorCourse);
  },
);

export const selectedSortedTopicActivitiesByPageId = createAppSelector(
  [(state, pageId: string) => pageId, selectActivities, selectPages, selectCurrentUserRole],
  (pageId, activities, pages, userRole) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    if (!pageId) return [];

    const foundPage = pages.find((page) => page.id === pageId);
    if (!foundPage) return [];

    const foundActivity = activities.find((activity) => activity.id === foundPage.activity_id);
    if (!foundActivity) return [];

    return sortTopicActivities(foundActivity.topic_id, activities, pages, canAuthorCourse);
  },
);

export const selectActivitiesForCurrentCourse = createAppSelector(
  [selectCurrentCourseId, selectActivities],
  (courseId, activities) => activities.filter((activity) => activity.course_id === courseId),
);

export const selectSortedTopicActivitiesForCurrentCourse = createAppSelector(
  [
    (state, pageFilter: (page: ActivityPageType) => boolean) => pageFilter,
    selectTopicsForCurrentCourse,
    selectActivities,
    selectPages,
    selectCurrentUserRole,
  ],
  (pageFilter, topics, activities, pages, userRole) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    return topics.reduce((topicsAccum, topic) => {
      const sortedActivities = activities
        .filter((activity) => activity.topic_id === topic.id)
        .sort(sortActivities);

      const activityWithPages = sortedActivities.reduce((activitiesAccum, activity) => {
        const activityPages = pages
          .filter((page) => page.activity_id === activity.id)
          .filter((page) => canAuthorCourse || page.released_at)
          .filter(pageFilter || (() => true));

        if (activityPages.length > 0) {
          activitiesAccum.push({ ...activity, pages: activityPages } as ActivityWithPagesType);
        }
        return activitiesAccum;
      }, [] as ActivityWithPagesType[]);

      if (activityWithPages.length > 0) {
        topicsAccum.push({ ...topic, activities: activityWithPages } as TopicWithActivitiesType);
      }
      return topicsAccum;
    }, [] as TopicWithActivitiesType[]);
  },
);

export const selectPageViewData = createAppSelector(
  [
    (state, page: PageType) => page,
    (state, page: PageType, topicIdParam?: string) => topicIdParam,
    (state, page: PageType) => selectPagesForCurrentCourse(state, page?.category),
    (state, page: PageType) => selectedSortedTopicActivitiesByPageId(state, page?.id),
    selectTopicsForCurrentCourse,
    selectCurrentCourseId,
    selectActivities,
    selectPages,
    selectCurrentUserRole,
  ],
  (
    page,
    topicIdParam,
    coursePages,
    topicActivities,
    topics,
    currentCourseId,
    allActivities,
    allPages,
    userRole,
  ) => {
    const canAuthorCourse = hasCourseCapability(userRole, Capability.CAN_AUTHOR_COURSE);
    const topicFromQuery = topics.find((topic) => topic.id === topicIdParam);
    const topicId = topicActivities[0]?.topic_id || topicFromQuery?.id || topics[0]?.id;

    const defaultTopicActivities = sortTopicActivities(
      topicId,
      allActivities,
      allPages,
      canAuthorCourse,
    );
    const defaultActivityPages = getSortedPages(defaultTopicActivities);

    if (!page || (currentCourseId && page.course_id !== currentCourseId)) {
      return {
        selectedPage: null,
        pages: defaultActivityPages,
        topicId,
      };
    }

    const activityPages = getSortedPages(topicActivities);
    const orderedCoursePages = coursePages.toSorted(DEFAULT_ORDER_FN);
    const pages = page?.category === "activity_page" ? activityPages : orderedCoursePages;
    return { selectedPage: page, pages, topicId };
  },
);

export const selectCurrentCourseHasMaterials = createAppSelector(
  [selectPagesForCurrentCourse, selectTopicsForCurrentCourse],
  (pages, topics) => {
    if (topics.length > 0) {
      return true;
    }
    return pages.filter((p) => isCoursePageCategory(p.category)).length > 0;
  },
);
